import React from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { Label, Icon } from '@nooz-optics/nooz-components-lib';
import { formatContentfulComponentProps } from '../../../../../../utils/globals';
import { UserState } from '../../../../../../utils/recoil/user';
import {
  IBlock,
  IColor,
  ILink,
} from '../../../../../../utils/types/contentful';
import { Notification } from '../../../../../Marketing/Notifications/notifications';
import { Divider } from '../../../../Elements/Divider';
import { Link } from '../../../../Elements/Link';
import { Image } from '../../../../Media';
import { List } from '../../../../Navigation/List/list';
import { LanguageCurrencySwitch } from '../../../LanguageCurrencySwitch';
import { ActionHelp } from '../../Actions/Help';
import { collectionFiltersState } from '../../../../../../utils/recoil/collection';
import { DrawerFloatingCloseButton } from '../../../../Overlays/Drawer/Drawer';
import { useDrawer } from '../../../../../../utils/recoil/drawer';

interface MobileHeaderNavigationProps {
  navigations: IBlock;
  actions: IBlock;
  banner: IBlock;
}

interface NavigationDetailsList {
  link: ILink;
  details: IBlock;
  color: IColor;
}

const BackButton = ({ prevDrawer, navigations }) => {
  const drawer = useDrawer();
  return (
    <button
      type='button'
      className='flex items-center mb-4'
      onClick={() => {
        drawer.open(prevDrawer);
      }}>
      <Icon
        iconKey='HiOutlineChevronLeft'
        size='xl'
      />
      <Label
        {...formatContentfulComponentProps(navigations).subLabel}
        decoration='underline'
        type='span'
        size='lg'
      />
    </button>
  );
};

const MobileHeaderCollectionNavigation = ({ navigations }) => {
  const drawer = useDrawer();
  const setFilters = useSetRecoilState(collectionFiltersState);

  const { list } = formatContentfulComponentProps(navigations);

  const listItems = list?.items.map((item) => {
    const { link, details, color }: NavigationDetailsList =
      formatContentfulComponentProps(item);

    // Item has not details, render a simple link
    if (!details) {
      return {
        spacing: 'py-6',
        children: (
          <div
            onClick={drawer.close}
            className='w-full px-3 z-10'>
            <Link
              {...link}
              color={{ classname: 'primary' }}
              weight='bold'>
              <div
                onClick={() => setFilters({})}
                className='w-full flex justify-between items-center'>
                <Label
                  {...link.label}
                  size='3xl'
                  weight='bold'
                />
                <Icon
                  color={color}
                  iconKey='HiChevronRight'
                  size='2xl'
                />
              </div>
            </Link>
          </div>
        ),
      };
    }

    // Item has details, render the details list
    const menuListItems = details.items.map((it, index) => {
      const { image } = formatContentfulComponentProps(it.items[1]);
      return (
        <>
          {index === 0 && (
            <div className='h-32 my-4 w-full flex items-center overflow-hidden rounded-xl'>
              <Image
                {...image}
                objectFit='cover'
                height='100%'
                width='100%'
              />
            </div>
          )}
          <List
            listItems={it.items.map((detail: any) => {
              const i = formatContentfulComponentProps(detail);

              if (detail.text) {
                return {
                  spacing: 'py-3',
                  children: (
                    <Divider
                      label={{
                        ...detail,
                        color: { classname: 'gray-800' },
                        weight: 'medium',
                        size: '3xl',
                      }}
                    />
                  ),
                };
              }

              return {
                spacing: 'py-2',
                children: (
                  <div
                    onClick={drawer.close}
                    className='flex justify-between w-full'>
                    <Link
                      {...i.link}
                      type='Page'
                      width='w-full'
                      label={{
                        ...i.link.label,
                        icon: null,
                        weight: i.link.label.color ? 'bold' : 'normal',
                        size: 'inherit',
                      }}
                    />
                    <Icon
                      iconKey='HiChevronRight'
                      size='xl'
                    />
                  </div>
                ),
              };
            })}
          />
        </>
      );
    });

    const menuButton = (
      <button
        type='button'
        onClick={() =>
          drawer.open({
            title: { ...link?.label, weight: 'medium', color },
            body: (
              <div className='w-full p-5 relative overflow-y-scroll no-scrollbar'>
                <BackButton
                  prevDrawer={drawer.value}
                  navigations={navigations}
                />
                {menuListItems}
                <DrawerFloatingCloseButton />
              </div>
            ),
            placement: 'left',
          })
        }
        className='flex w-full justify-between items-center px-3 z-10'>
        <Label
          {...link?.label}
          color={{ classname: 'primary' }}
          weight='bold'
          size='3xl'
        />
        <Icon
          color={color}
          iconKey='HiChevronRight'
          size='2xl'
        />
      </button>
    );

    return {
      spacing: 'py-6',
      children: menuButton,
    };
  });
  return (
    <List
      divide
      listItems={listItems}
    />
  );
};

const MobileHeaderActionNavigation = ({ actions, navigations }) => {
  const user = useRecoilValue(UserState);
  const drawer = useDrawer();
  const { list } = formatContentfulComponentProps(actions);
  const listItems = list?.items.map((item) => {
    // Menu is of type Button
    if (item.slug) {
      if (
        (user.email && item.key === 'profile') ||
        (!user.email && item.key === 'login')
      ) {
        return {
          spacing: 'py-4',
          children: (
            <div
              onClick={drawer.close}
              className='px-3'>
              <Link
                {...item}
                type='Page'
                label={{
                  ...item.label,
                  weight: 'medium',
                  size: '2xl',
                }}
              />
            </div>
          ),
        };
      }
    }

    const { link, details } = formatContentfulComponentProps(item);

    // Details menu
    if (link) {
      const menuDetailsListItems = details.items.map((detail) => {
        const i = formatContentfulComponentProps(detail);

        // Notification
        if (i.image) {
          return {
            spacing: 'py-2',
            children: (
              <div onClick={drawer.close}>
                <Notification {...i} />
              </div>
            ),
          };
        }

        // Help detail
        if (i.link) {
          return {
            spacing: 'py-3',
            children: (
              <div onClick={drawer.close}>
                <ActionHelp {...i} />
              </div>
            ),
          };
        }
      });

      const menuActionsListItems = formatContentfulComponentProps(
        item
      ).actions?.items.map((action) => {
        const i = formatContentfulComponentProps(action);

        // Help action
        if (i.link) {
          return {
            spacing: 'py-3',
            children: (
              <div onClick={drawer.close}>
                <ActionHelp {...i} />
              </div>
            ),
          };
        }
      });

      let menuListItems;
      if (menuActionsListItems) {
        menuListItems = [...menuDetailsListItems, ...menuActionsListItems];
      } else {
        menuListItems = menuDetailsListItems;
      }

      const menuButton = (
        <button
          type='button'
          onClick={() =>
            drawer.open({
              title: { ...link?.label, weight: 'medium' },
              body: (
                <div className='w-full p-5 h-5/6 relative overflow-y-scroll no-scrollbar'>
                  <BackButton
                    prevDrawer={drawer.value}
                    navigations={navigations}
                  />
                  <List
                    divide
                    listItems={menuListItems}
                  />
                  <DrawerFloatingCloseButton />
                </div>
              ),
              placement: 'left',
            })
          }
          className='flex w-full justify-between items-center px-3 z-10'>
          <Label
            {...link?.label}
            weight='medium'
            size='2xl'
          />
          <Icon
            iconKey='HiChevronRight'
            size='2xl'
          />
        </button>
      );

      return {
        spacing: 'py-4',
        children: menuButton,
      };
    }
  });
  return (
    <List
      divide
      listItems={listItems}
    />
  );
};

const MobileHeaderBannerNavigation = ({ banner }) => {
  const drawer = useDrawer();
  const listItems = formatContentfulComponentProps(banner).list?.items.map(
    (item) => {
      // Menu is of type Button

      if (item.key === 'languageCurrencySwitch') {
        return {
          spacing: 'py-5',
          children: (
            <LanguageCurrencySwitch {...formatContentfulComponentProps(item)} />
          ),
        };
      }

      // Details menu
      if (item.key === 'actions') {
        const { link } = formatContentfulComponentProps(item);
        return {
          spacing: 'py-5',
          children: (
            <div
              onClick={drawer.close}
              className='flex px-3'>
              <Link
                {...link}
                label={{ ...link?.label, weight: 'medium', size: '2xl' }}
              />
            </div>
          ),
        };
      }
    }
  );
  return (
    <List
      divide
      listItems={listItems}
    />
  );
};

export const MobileHeaderNavigation: React.FC<MobileHeaderNavigationProps> = ({
  navigations,
  actions,
  banner,
}) => (
  <div className='w-full p-5 relative overflow-y-scroll no-scrollbar'>
    {/* Main collection navigation */}
    <Divider label={formatContentfulComponentProps(navigations).label} />
    <MobileHeaderCollectionNavigation navigations={navigations} />
    {/* Header haderion navigation */}
    <Divider />
    <MobileHeaderActionNavigation
      actions={actions}
      navigations={navigations}
    />
    <MobileHeaderBannerNavigation banner={banner} />
    {/* Floating close button */}
    <DrawerFloatingCloseButton />
  </div>
);
